import { useState, useEffect, SyntheticEvent, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import {
  TripLeg,
  TripLegAddress,
  OnHoldTripLegApproveDenyAction,
} from '../../../data/models/reservation.model';
import { TripLegStatus } from '../../../data/models/trip-leg-status.model';
import { OverrideRequest } from '../../../data/models/override-requests-model';
import ConfirmationDialog from '../../ConfirmationDialog';
import * as reservationSlice from '../../../data/reservation.slice';
import * as userSlice from '../../../data/user.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as triplegSlice from '../../../data/tripleg.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useLookups } from '../../../hooks/useLookups';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { TripLegEventType } from '../../../services/webSocketService/updateTripLeg';

const PREFIX = 'OverrideSection';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  tabheader: `${PREFIX}-tabheader`,
  tabcell: `${PREFIX}-tabcell`,
  tableAlternateText: `${PREFIX}-tableAlternateText`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 400,
    width: '100%',
  },
  [`& .${classes.header}`]: {
    fontFamily: 'Bitter',
    fontSize: '30px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '40px',
    letterSpacing: '0.25px',
    paddingBottom: theme.spacing(5),
    paddingLeft: theme.spacing(1),
  },
  [`& .${classes.subheader}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.17px',
  },
  [`& .${classes.tabheader}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
    letterSpacing: '0.4px',
  },
  [`& .${classes.tabcell}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.tableAlternateText}`]: {
    margin: theme.spacing(3),
  },
}));

const OverrideSection = () => {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const [currentTab, setCurrentTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<OverrideRequest | null>(
    null,
  );
  const [dialogResult, setDialogResult] = useState(false);

  useLookups(['tripLegOverrideReason']);
  const overrideReasons = useAppSelector(
    lookupSlice.selectOverrideAuthorizationReasons,
  );

  const allTripLegs = useAppSelector(reservationSlice.selectAllTripLegs);
  const draftTripLegs = useAppSelector(reservationSlice.selectDraftTripLegs);
  const isSavedTripLeg = useAppSelector(reservationSlice.selectSavedTripLeg);
  const servicePlanList = useAppSelector(clientSlice.selectServicePlanList);
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );

  const tripLegs = useMemo(() => {
    if (currentTab === 0) {
      return allTripLegs.filter(
        (tripLeg: TripLeg) => tripLeg.status === TripLegStatus.OnHold,
      );
    } else {
      return draftTripLegs.filter(
        (tripLeg: TripLeg) => tripLeg.status === TripLegStatus.OnHold,
      );
    }
  }, [allTripLegs, draftTripLegs, currentTab]);

  const confirmDeny = () => {
    setOpenDialog(false);
  };

  const confirmApproval = () => {
    if (!currentRequest) {
      return;
    }
    dispatch(
      reservationSlice.approveOrDenyTripLegsAsync(
        currentRequest.passengerUUID,
        currentRequest.reservationReferenceID,
        currentRequest.tripLegsReferenceIDs,
        currentRequest.action,
      ),
    );
    setOpenDialog(false);
  };

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleOpenDialog = (request: OverrideRequest) => {
    const result = OnHoldTripLegApproveDenyAction.Approve === request.action;
    setDialogResult(result);
    setCurrentRequest(request);
    setOpenDialog(true);
  };

  const RenderRequesterName = ({ tripLeg }: { tripLeg: TripLeg }) => {
    let name = t('csaSupervisor.unknown');
    if (tripLeg.overrides.length > 0) {
      name = `${tripLeg.overrides[0].clientFirstName} ${tripLeg.overrides[0].clientLastName}`;
    }
    return <>{name}</>;
  };

  const RenderAddress = ({ address }: { address: TripLegAddress }) => {
    return (
      <>{`${address.addressLine1} ${address.city}, ${address.stateCode}`}</>
    );
  };

  const createOverrideRequestObj = (
    tripLeg: TripLeg,
    approveOrDeny: OnHoldTripLegApproveDenyAction,
  ): OverrideRequest => {
    const request: OverrideRequest = {
      passengerUUID: tripLeg.passengerUUID,
      reservationReferenceID: tripLeg.reservationReferenceID!,
      tripLegsReferenceIDs: [tripLeg.tripLegReferenceID!],
      action: approveOrDeny,
    };
    return request;
  };

  const getServicePlanName = (id: string) => {
    if (servicePlanList) {
      const sp = servicePlanList.servicePlans.find(
        (s) => s.servicePlanUUID === id,
      );
      return sp ? sp.name : '';
    }
  };

  const getServicePlans = (tripLeg: TripLeg) => {
    return tripLeg.servicePlans
      ?.filter((sp) =>
        tripLeg.overrides
          .map((override) => override.servicePlanUUID)
          .includes(sp.servicePlanUUID),
      )
      .map((sp) => getServicePlanName(sp.servicePlanUUID))
      ?.join(', ');
  };

  const RenderTripTable = ({ tripLegs }: { tripLegs: TripLeg[] }) => {
    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.requester')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.tripLegID')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.tripDate')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.pickupLocation')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.dropoffLocation')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.reason')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.servicePlan')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('csaSupervisor.currentStatus')}
            </TableCell>
            <TableCell className={classes.subheader}>
              {t('common.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tripLegs.map((tripLeg, index) => {
            const overrideReason =
              overrideReasons.find(
                (reason) =>
                  reason.tripLegOverrideReasonBC ===
                  tripLeg.overrides[0]?.tripLegOverrideReasonBC,
              )?.tripLegOverrideReasonName ||
              tripLeg.overrides[0]?.tripLegOverrideReasonBC;
            return (
              <TableRow key={index}>
                <TableCell className={classes.tabcell}>
                  <RenderRequesterName tripLeg={tripLeg} />
                </TableCell>
                <TableCell className={classes.tabcell}>
                  {tripLeg.tripLegReferenceID}
                </TableCell>
                <TableCell className={classes.tabcell}>
                  {tripLeg.appointmentDate}
                </TableCell>
                <TableCell className={classes.tabcell}>
                  <RenderAddress address={tripLeg.pickUpAddress} />
                </TableCell>
                <TableCell className={classes.tabcell}>
                  <RenderAddress address={tripLeg.dropOffAddress} />
                </TableCell>
                <TableCell className={classes.tabcell}>
                  {overrideReason}
                </TableCell>
                <TableCell className={classes.tabcell}>
                  {getServicePlans(tripLeg)}
                </TableCell>
                <TableCell className={classes.tabcell}>
                  {t(`tripLegStatus.${tripLeg.status}`)}
                </TableCell>
                <TableCell>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center">
                    <Button
                      variant="contained"
                      onClick={() =>
                        handleOpenDialog(
                          createOverrideRequestObj(
                            tripLeg,
                            OnHoldTripLegApproveDenyAction.Approve,
                          ),
                        )
                      }
                      style={{ marginRight: '10px' }}>
                      {t('csaSupervisor.approve')}
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() =>
                        handleOpenDialog(
                          createOverrideRequestObj(
                            tripLeg,
                            OnHoldTripLegApproveDenyAction.Deny,
                          ),
                        )
                      }>
                      {t('csaSupervisor.deny')}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const fetchTripLegs = () => {
    if (currentTab === 0) {
      const dateString = new Date(Date.now()).toISOString().split('T')[0];
      dispatch(reservationSlice.getAllTripLegsAsync('draft', dateString));
    } else {
      dispatch(reservationSlice.getDraftTripLegsAsync('onhold'));
    }
  };

  useEffect(() => {
    const clientBC = tripLegs[0]?.clientBC;
    if (clientBC && !servicePlanList?.servicePlans.length) {
      dispatch(clientSlice.fetchClientServicePlansAsync(clientBC));
    }
  }, [tripLegs, servicePlanList]);

  useEffect(() => {
    fetchTripLegs();
  }, [currentTab]);

  useEffect(() => {
    if (isSavedTripLeg) {
      fetchTripLegs();
      dispatch(reservationSlice.setSavedTripLeg(false));
    }
  }, [isSavedTripLeg, fetchTripLegs]);

  useEffect(() => {
    if (
      tripLegEventEmitted &&
      (tripLegEventEmitted.detailType === TripLegEventType.TripLegHeld ||
        tripLegEventEmitted.detailType ===
          TripLegEventType.OnHoldTripLegUpdated ||
        tripLegEventEmitted.detailType === TripLegEventType.TripLegOpened ||
        tripLegEventEmitted.detailType === TripLegEventType.TripLegDenied)
    ) {
      fetchTripLegs();
      dispatch(triplegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEventEmitted]);

  return (
    <StyledBox className={classes.container}>
      <Card>
        <CardContent>
          <Typography className={classes.header}>
            {t('csaSupervisor.overrideApproval')}
          </Typography>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="trip tabs">
            <Tab label="Today’s Overrides" className={classes.tabheader} />
            <Tab label="All Requests" className={classes.tabheader} />
          </Tabs>
          {tripLegs.length > 0 ? (
            <RenderTripTable tripLegs={tripLegs} />
          ) : (
            <Typography className={classes.tableAlternateText}>
              {t('csaSupervisor.noOverrides')}
            </Typography>
          )}
        </CardContent>
      </Card>
      <ConfirmationDialog
        title={
          dialogResult
            ? t('csaSupervisor.confirmationOverrideApproveTitle')
            : t('csaSupervisor.confirmationOverrideDenyTitle')
        }
        message={
          dialogResult
            ? t('csaSupervisor.confirmationQuestionApprove')
            : t('csaSupervisor.confirmationQuestionDeny')
        }
        open={openDialog}
        positiveAction={confirmApproval}
        negativeAction={confirmDeny}
      />
    </StyledBox>
  );
};

export default OverrideSection;
